import type { SalesforceObjectSummary, SalesforceFieldOption } from './SalesforceObjectConfigurationAssistant.access';
import { $Resources } from '@cwd/lib/main';

// Pure relationship-detection logic for the "Related records" step - no Vue
// dependencies, so it's reasoned about (and could be tested) independently of
// the component's reactive state. See
// docs/superpowers/specs/2026-09-14-salesforce-assistant-autodetect-relationships-design.md.

// field lives on the object being linked (the "target"), pointing AT the
// contact object. Maps 1:1 onto ChildObjectConfiguration.ParentField in the
// backend (SalesforceObjectConfiguration.cs) - resolved by reading that
// field's own value directly off the target's row as its ContactSfId.
export interface DirectLinkCandidate {
    kind: 'direct';
    field: string;
}

// The lookup direction here is the one most likely to get inverted by a
// future "cleanup" - read this carefully. `field` lives on `viaObject` (an
// object ALREADY ADDED to the assistant), and viaObject.field points AT the
// object being linked (the "target"), not the other way around. Maps onto
// ChildObjectConfiguration.ParentVia { ChildObject: viaObject, Field: field }
// in the backend, resolved by finding the ALREADY-RESOLVED viaObject row
// whose own `field` value equals the target row's SfId, then copying
// viaObject's ContactSfId onto the target. Concretely, for WMSE:
// viaObject = Membership_Instance__c, field = Gift_Transaction__c
// (Membership_Instance__c's own lookup field,
// which points at a GiftTransaction row) - GiftTransaction itself has no
// lookup back to Membership_Instance__c or the contact.
export interface ViaLinkCandidate {
    kind: 'via';
    viaObject: string;
    field: string;
}

export type LinkCandidate = DirectLinkCandidate | ViaLinkCandidate;

function filterReferencing(fields: SalesforceFieldOption[], targetObject: string): SalesforceFieldOption[] {
    return fields.filter((f) => f.ReferenceTo?.includes(targetObject));
}

// Finds every way `targetObject` could connect back to `contactObject`:
// - A direct lookup field on targetObject itself pointing at contactObject.
// - A field on any of `otherObjects` (objects already added to the
//   assistant) that points at targetObject - i.e. targetObject is reached
//   "through" that other, already-connected object.
//
// Prefers a direct match over an indirect one when both exist - simpler, and
// doesn't depend on another object staying configured. This is a default,
// not a claim that direct is "more correct": tested live against the real
// WMSE org, GiftTransaction has both a direct DonorId lookup to Account and
// an indirect path through Membership_Instance__c, and they link very
// different subsets of records (DonorId: all 7,308 GiftTransaction rows /
// 3,189 contacts; the Membership Instance path: only 784 / 555 - gifts with
// no associated membership never link that way). Which one a customer
// actually means is a business question this metadata can't answer -
// defaulting to direct just picks a starting point; the "Not right? Change
// manually" override (always shown) is how that gets corrected once someone
// who knows the customer's data says so.
//
// Only one level of indirection is detected - matches the one-hop limit the
// backend's ParentVia resolution actually supports today. See the design
// doc's "explicitly out of scope" section: extending
// this to a longer chain is confirmed easy later, tied to a backend fix, not
// something this function needs to anticipate.
export function detectLinkCandidates(
    targetObject: string,
    contactObject: string,
    otherObjects: { object: string }[],
    fieldsByObject: Record<string, SalesforceFieldOption[]>
): LinkCandidate[] {
    const ownFields = fieldsByObject[targetObject] ?? [];
    const direct = filterReferencing(ownFields, contactObject).map((f): DirectLinkCandidate => ({ kind: 'direct', field: f.Name }));
    if (direct.length > 0) return direct;

    const via: ViaLinkCandidate[] = [];
    for (const other of otherObjects) {
        if (other.object === targetObject) continue;
        const otherFields = fieldsByObject[other.object] ?? [];
        for (const f of filterReferencing(otherFields, targetObject)) {
            via.push({ kind: 'via', viaObject: other.object, field: f.Name });
        }
    }
    return via;
}

// Always the Salesforce Label, never the API/internal field name - an
// explicit product decision (see the design doc): showing the internal name
// was considered and rejected as worse UX than the mispick risk it guards
// against.
export function describeCandidate(
    candidate: LinkCandidate,
    targetObject: string,
    availableObjects: SalesforceObjectSummary[],
    fieldsByObject: Record<string, SalesforceFieldOption[]>
): string {
    const fieldLabel = (objectName: string, fieldName: string) =>
        (fieldsByObject[objectName] ?? []).find((f) => f.Name === fieldName)?.Label ?? fieldName;
    const objectLabel = (objectName: string) => availableObjects.find((o) => o.Name === objectName)?.Label ?? objectName;

    if (candidate.kind === 'direct') {
        return $Resources.SalesforceAssistantDirectLinkDescription.replace('{{field}}', fieldLabel(targetObject, candidate.field));
    }
    return $Resources.SalesforceAssistantViaLinkDescription
        .replace('{{object}}', objectLabel(candidate.viaObject))
        .replace('{{field}}', fieldLabel(candidate.viaObject, candidate.field));
}
